import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { addToCart, setCartOpen } from '../../store/slices/cartSlice';
import { selectAllProducts } from '../../store/slices/productsSlice';
import { isPurchasableProduct } from '../../utils/productRules';
import { calculateExtensionPrice, formatDurationLabel, getDurationDays, durationKeyFromDays, resolvePriceForValidity, resolveAddonPriceForBaseValidity, sortDurationKeys } from '../../utils/helpers';

const ExtensionModal = ({ isOpen, onClose, data }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const allProducts = useSelector(selectAllProducts);
    const extensionDiscountConfig = useSelector((state) => state.settings?.settings?.extensionDiscount || {});

    // Memoize product index for quick lookup
    const productIndex = useMemo(() => {
        const map = {};
        (allProducts || []).forEach((item) => {
            map[item.id] = item;
        });
        return map;
    }, [allProducts]);

    const mode = data?.mode === 'renew' ? 'renew' : 'extend';
    const isRenewMode = mode === 'renew';
    const { productId = '', productName = '', basePrice = 0, originalOrderId = '', currentValidity = '', originalPaidPrice = null, remainingDays = null } = data || {};
    const extensionDiscountEligible = Boolean(data?.extensionDiscountEligible);
    const product = productId ? productIndex[productId] : null;
    const [selectedAddonIds, setSelectedAddonIds] = useState([]);
    const currentDurationDays = getDurationDays(currentValidity);
    const monthlyDurationDays = getDurationDays('monthly');
    const extensionOptions = useMemo(() => {
        const isAllowedExtensionPeriod = (_period) => {
            // Allow users to renew or extend with ANY validity tier offered by product (including short validities: 3d, 7d, 15d)
            return true;
        };

        const priceMap = product?.pricingRaw || product?.Price || null;
        const monthlyPrice = resolvePriceForValidity(priceMap || {}, 'monthly');
        const customPrice = resolvePriceForValidity(priceMap || {}, currentValidity);
        const paidPrice = Number(originalPaidPrice ?? customPrice ?? basePrice) || 0;
        const remaining = Number(remainingDays);
        const canUpgradeToMonthly = (
            currentDurationDays !== null &&
            monthlyDurationDays !== null &&
            currentDurationDays > 0 &&
            currentDurationDays < monthlyDurationDays &&
            Number.isFinite(remaining) &&
            remaining > 0 &&
            Number(monthlyPrice) > paidPrice
        );

        const upgradeOption = canUpgradeToMonthly
            ? [{
                period: `${monthlyDurationDays - currentDurationDays}d`,
                price: Number((Number(monthlyPrice) - paidPrice).toFixed(2)),
                type: 'monthly-upgrade',
                fromValidity: currentValidity,
                toValidity: 'monthly',
                upgradeDays: monthlyDurationDays - currentDurationDays,
                monthlyPrice: Number(monthlyPrice),
                paidPrice
            }]
            : [];

        if (priceMap && typeof priceMap === 'object') {
            const standardOptions = sortDurationKeys(Object.keys(priceMap))
                .map((period) => ({ period, price: resolvePriceForValidity(priceMap, period) }))
                .filter(({ period, price }) => String(period || '').trim() && Number(price) > 0 && isAllowedExtensionPeriod(period));
            return [...upgradeOption, ...standardOptions];
        }

        const fallbackOptions = Number(basePrice) > 0
            ? [{ period: 'monthly', price: Number(basePrice) }]
            : [];
        return [...upgradeOption, ...fallbackOptions];
    }, [basePrice, currentDurationDays, currentValidity, monthlyDurationDays, originalPaidPrice, product, remainingDays]);
    const hasMonthlyUpgradeOption = extensionOptions.some((option) => option.type === 'monthly-upgrade');
    const activeAddons = useMemo(() => {
        const source = Array.isArray(data?.addons) ? data.addons : Array.isArray(product?.addons) ? product.addons : [];
        return source
            .filter((addon) => String(addon?.status || 'active').toLowerCase() === 'active')
            .filter((addon) => addon?.id);
    }, [data?.addons, product?.addons]);
    const addonAccessMap = useMemo(
        () => data?.addonAccessMap && typeof data.addonAccessMap === 'object' ? data.addonAccessMap : {},
        [data?.addonAccessMap]
    );
    const isAddonOwned = useCallback((addonId) => {
        const access = addonAccessMap?.[addonId];
        if (!access) return false;
        const status = String(access.status || 'active').toLowerCase();
        if (status === 'revoked' || status === 'inactive') return false;
        if (access.expiresAt === null || access.expiresAt === 'lifetime') return true;
        const expiresAt = access.expiresAt ? new Date(access.expiresAt).getTime() : 0;
        return !expiresAt || expiresAt > Date.now();
    }, [addonAccessMap]);
    const getCombinedAddonValidity = useCallback((period) => {
        const periodDays = getDurationDays(period);
        if (periodDays === null) return period;
        const remaining = Number(remainingDays);
        if (Number.isFinite(remaining) && remaining > 0) {
            return durationKeyFromDays(remaining + periodDays);
        }
        return period;
    }, [remainingDays]);
    const getAddonValidityForPeriod = useCallback((addon, period) => (
        isAddonOwned(addon?.id) ? period : getCombinedAddonValidity(period)
    ), [getCombinedAddonValidity, isAddonOwned]);
    const getAddonPriceForPeriod = useCallback((addon, period) => resolveAddonPriceForBaseValidity(
        addon?.pricingRaw || addon?.Price || {},
        product?.pricingRaw || product?.Price || {},
        getAddonValidityForPeriod(addon, period)
    ), [getAddonValidityForPeriod, product?.Price, product?.pricingRaw]);
    const selectedAddons = useMemo(
        () => activeAddons.filter((addon) => selectedAddonIds.includes(addon.id)),
        [activeAddons, selectedAddonIds]
    );
    const getSelectedAddonTotal = useCallback((period) => selectedAddons.reduce((sum, addon) => {
        const addonPrice = getAddonPriceForPeriod(addon, period).price;
        return sum + (Number(addonPrice) || 0);
    }, 0), [getAddonPriceForPeriod, selectedAddons]);

    useEffect(() => {
        if (!isOpen) {
            setSelectedAddonIds([]);
            return;
        }

        const selectableAddonIds = activeAddons
            .filter((addon) => extensionOptions.some((option) => getAddonPriceForPeriod(addon, option.period).price !== null))
            .map((addon) => addon.id);
        setSelectedAddonIds(selectableAddonIds);
    }, [activeAddons, data?.productId, extensionOptions, getAddonPriceForPeriod, isOpen]);

    const toggleAddon = (addonId) => {
        setSelectedAddonIds((current) => current.includes(addonId)
            ? current.filter((id) => id !== addonId)
            : [...current, addonId]);
    };

    if (!isOpen || !data) return null;

    const handleExtendValidity = (option) => {
        if (product && !isPurchasableProduct(product)) {
            onClose();
            return;
        }
        const validity = option.period;
        const periodPrice = option.price;
        const isUpgrade = option.type === 'monthly-upgrade';
        const finalPrice = isUpgrade || isRenewMode || !extensionDiscountEligible
            ? Number(periodPrice)
            : calculateExtensionPrice(periodPrice, validity, extensionDiscountConfig);

        // Find image from product index
        const image = product?.image || product?.['icon link'] || '';

        const extensionProduct = {
            id: `${productId}-extension`,
            name: `${productName} ${isRenewMode ? 'Renewal' : 'Extension'}`,
            price: finalPrice,
            image: image,
            category: 'extension',
            extensionFor: originalOrderId,
            renewal: isRenewMode,
            extensionDiscountEligible,
            accessSource: data?.accessSource || null,
            upgradeFromValidity: option.fromValidity || null,
            upgradeToValidity: option.toValidity || null,
            upgradeDays: option.upgradeDays || null,
            remainingDays: Number.isFinite(Number(remainingDays)) ? Number(remainingDays) : null
        };

        dispatch(addToCart({ product: extensionProduct, validity }));
        selectedAddons.forEach((addon) => {
            const addonValidity = getAddonValidityForPeriod(addon, validity);
            const addonPriceInfo = getAddonPriceForPeriod(addon, validity);
            if (addonPriceInfo.price === null || addonPriceInfo.price < 0) return;
            const addonAlreadyOwned = isAddonOwned(addon.id);
            dispatch(addToCart({
                product: {
                    id: `${productId}::addon::${addon.id}`,
                    name: addon.name,
                    price: addonPriceInfo.price,
                    image: addon.image || image,
                    category: 'addon',
                    addonFor: productId,
                    addonId: addon.id,
                    addonName: addon.name,
                    baseProductName: productName,
                    addonPricingSource: addonAlreadyOwned ? 'extension_existing_addon' : isRenewMode ? 'renewal' : isUpgrade ? 'extension_upgrade' : 'extension',
                    addonAdjustedDays: addonPriceInfo.days || null,
                    addonBaseValidity: addonValidity
                },
                validity: addonValidity
            }));
        });
        onClose();
        dispatch(setCartOpen(true));
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.6 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black z-[95]"
                onClick={onClose}
            />
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="fixed inset-0 z-[96] flex items-center justify-center p-4"
                onClick={(e) => e.target === e.currentTarget && onClose()}
            >
                <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-6 relative">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                            {hasMonthlyUpgradeOption && !isRenewMode ? 'Extend to Monthly' : isRenewMode ? t('orders.renew', 'Renew') : t('orders.extend', 'Extend')}
                        </h3>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>

                    {product && !isPurchasableProduct(product) && (
                        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-semibold text-amber-800 dark:border-amber-800/40 dark:bg-amber-900/20 dark:text-amber-300">
                            {t('product.unavailable', 'This product is currently unavailable for renewals or extensions.')}
                        </div>
                    )}

                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        {hasMonthlyUpgradeOption && !isRenewMode ? 'Upgrade active custom validity for:' : isRenewMode ? t('orders.renew_for', 'Renew for:') : t('orders.extend_for', 'Extend for:')} <strong>{productName}</strong>
                    </p>

                    {activeAddons.length > 0 && (
                        <div className="mb-4 rounded-2xl border border-violet-200 bg-violet-50/70 p-3 dark:border-violet-500/20 dark:bg-violet-500/10">
                            <div className="mb-2 text-xs font-black uppercase tracking-wide text-violet-700 dark:text-violet-200">
                                Include Addons
                            </div>
                            <div className="space-y-2">
                                {activeAddons.map((addon) => {
                                    const owned = isAddonOwned(addon.id);
                                    const hasAnyPrice = extensionOptions.some((option) => getAddonPriceForPeriod(addon, option.period).price !== null);
                                    const disabled = !hasAnyPrice;
                                    return (
                                        <label key={addon.id || `addon-${addon.name || 'feature'}`} className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 ${selectedAddonIds.includes(addon.id)
                                            ? 'border-violet-400 bg-white dark:bg-gray-950/40'
                                            : 'border-violet-100 bg-white/70 dark:border-violet-500/10 dark:bg-gray-950/20'
                                        } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={selectedAddonIds.includes(addon.id)}
                                                disabled={disabled}
                                                onChange={() => toggleAddon(addon.id)}
                                            />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="truncate text-sm font-bold text-gray-900 dark:text-white">{addon.name}</span>
                                                    {owned && (
                                                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-200">Owned</span>
                                                    )}
                                                </div>
                                                <p className="text-[11px] text-gray-500 dark:text-gray-400">Addon validity follows the extension option you choose.</p>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="space-y-3">
                        {extensionOptions.map((option) => {
                            const { period, price: currentPrice } = option;
                            const isUpgrade = option.type === 'monthly-upgrade';
                            const finalPrice = isUpgrade || isRenewMode || !extensionDiscountEligible
                                ? Number(currentPrice)
                                : calculateExtensionPrice(currentPrice, period, extensionDiscountConfig);
                            const hasDiscount = extensionDiscountEligible && !isRenewMode && finalPrice < currentPrice;
                            const addonTotal = getSelectedAddonTotal(period);
                            const combinedTotal = finalPrice + addonTotal;

                            return (
                                <button
                                    key={`${option.type || 'extension'}-${period || option.toValidity || option.price}`}
                                    onClick={() => handleExtendValidity(option)}
                                    className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors text-left"
                                >
                                    <div className="flex justify-between items-center gap-3">
                                        <div>
                                            <span className="font-medium text-gray-900 dark:text-white capitalize">
                                                {isUpgrade ? `Upgrade to ${formatDurationLabel(option.toValidity)}` : formatDurationLabel(period)}
                                            </span>
                                            {isUpgrade && (
                                                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                                    Pay only the difference and add {option.upgradeDays} days
                                                </p>
                                            )}
                                        </div>
                                        <div className="text-right notranslate" translate="no">
                                            <span className="text-primary-600 dark:text-primary-400 font-bold">${combinedTotal.toFixed(2)}</span>
                                            {hasDiscount && (
                                                <div className="text-xs text-gray-500 line-through">${Number(currentPrice).toFixed(2)}</div>
                                            )}
                                            {addonTotal > 0 && (
                                                <div className="text-[10px] text-violet-600 dark:text-violet-300">Includes addons ${addonTotal.toFixed(2)}</div>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {isRenewMode
                                ? t('orders.renewal_hint', 'Renewal will be added to your cart at regular price. Complete checkout to reactivate access.')
                                : hasMonthlyUpgradeOption
                                    ? 'Choose a duration to stack onto your active license, or select monthly upgrade to pay only the difference.'
                                : t('orders.extension_hint', 'Extension will be added to your cart. Complete checkout to activate the extension.')}
                        </p>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default ExtensionModal;
